import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Truck, LogIn, ArrowLeft, Check, X, Building2, Loader2, Gift, ChevronDown, ChevronUp, HardHat, Mail, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Country data with phone codes and states/provinces
const COUNTRIES = [
  { code: 'AU', name: 'Australia', phoneCode: '+61', states: ['Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia'] },
  { code: 'NZ', name: 'New Zealand', phoneCode: '+64', states: ['Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', 'Hawke\'s Bay', 'Manawatu-Wanganui', 'Marlborough', 'Nelson', 'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman', 'Waikato', 'Wellington', 'West Coast'] },
  { code: 'US', name: 'United States', phoneCode: '+1', states: ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'] },
  { code: 'CA', name: 'Canada', phoneCode: '+1', states: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'] },
  { code: 'GB', name: 'United Kingdom', phoneCode: '+44', states: ['England', 'Scotland', 'Wales', 'Northern Ireland'] },
  { code: 'IE', name: 'Ireland', phoneCode: '+353', states: ['Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry', 'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'] },
  { code: 'SG', name: 'Singapore', phoneCode: '+65', states: ['Central Region', 'East Region', 'North Region', 'North-East Region', 'West Region'] },
  { code: 'AE', name: 'United Arab Emirates', phoneCode: '+971', states: ['Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'] },
  { code: 'ZA', name: 'South Africa', phoneCode: '+27', states: ['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'] },
  { code: 'IN', name: 'India', phoneCode: '+91', states: ['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'] },
  { code: 'DE', name: 'Germany', phoneCode: '+49', states: ['Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'] },
  { code: 'FR', name: 'France', phoneCode: '+33', states: ['Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire', 'Corsica', 'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine', 'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur'] },
  { code: 'JP', name: 'Japan', phoneCode: '+81', states: ['Hokkaido', 'Tohoku', 'Kanto', 'Chubu', 'Kinki', 'Chugoku', 'Shikoku', 'Kyushu'] },
  { code: 'OTHER', name: 'Other', phoneCode: '', states: [] }
];

// Default tier configurations (will be overridden by Stripe pricing if configured)
const DEFAULT_TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 99,
    description: 'Perfect for small teams getting started',
    baseFeatures: [
      '1 User',
      '2 Operators',
      'Asset management',
      'Job scheduling',
      'Digital dockets',
      'Customer & site management',
      'Cloud Storage: 10 GB',
      'Help Centre & Support (Standard)'
    ],
    extraUserPrice: 49,
    extraOperatorPrice: 19,
    popular: false
  },
  {
    id: 'advanced',
    name: 'Advanced',
    price: 169,
    description: 'For growing businesses needing more power',
    baseFeatures: [
      '1 User',
      '2 Operators',
      'Everything in Basic, plus:',
      'Reports & analytics',
      'Compliance tracking',
      'Timesheets',
      'Team messaging',
      'Cloud Storage: 50 GB',
      'Help Centre & Support (Priority)'
    ],
    extraUserPrice: 69,
    extraOperatorPrice: 29,
    popular: true
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 199,
    description: 'Full access with AI and advanced features',
    baseFeatures: [
      '1 User',
      '2 Operators',
      'Everything in Advanced, plus:',
      'AI-powered features',
      'Smart suggestions',
      'OCR docket scanning',
      'API access (Coming Soon)',
      'Cloud Storage: 100 GB',
      'Help Centre & Support (Priority+)'
    ],
    extraUserPrice: 99,
    extraOperatorPrice: 29,
    popular: false
  }
];

// Feature comparison data for the matrix
const FEATURE_COMPARISON = [
  { 
    category: 'Core Features',
    features: [
      { name: 'Asset Management', basic: true, advanced: true, professional: true },
      { name: 'Operator Management', basic: true, advanced: true, professional: true },
      { name: 'Job Scheduling', basic: true, advanced: true, professional: true },
      { name: 'Digital Dockets', basic: true, advanced: true, professional: true },
      { name: 'Customer & Site Management', basic: true, advanced: true, professional: true },
      { name: 'Worker Types', basic: true, advanced: true, professional: true },
      { name: 'Basic Forms', basic: true, advanced: true, professional: true },
    ]
  },
  {
    category: 'Advanced Features',
    features: [
      { name: 'Reports & Analytics', basic: false, advanced: true, professional: true },
      { name: 'Compliance Tracking', basic: false, advanced: true, professional: true },
      { name: 'Timesheets', basic: false, advanced: true, professional: true },
      { name: 'Custom Forms Builder', basic: false, advanced: true, professional: true },
      { name: 'Team Messaging', basic: false, advanced: true, professional: true },
      { name: 'Subcontractor Management', basic: false, advanced: true, professional: true },
    ]
  },
  {
    category: 'Professional Features',
    features: [
      { name: 'AI-Powered Features', basic: false, advanced: false, professional: true },
      { name: 'Smart Suggestions', basic: false, advanced: false, professional: true },
      { name: 'OCR Docket Scanning', basic: false, advanced: false, professional: true },
      { name: 'API Access', basic: false, advanced: false, professional: 'soon' },
    ]
  },
  {
    category: 'Storage & Support',
    features: [
      { name: 'Document Storage', basic: '10 GB', advanced: '50 GB', professional: '100 GB' },
      { name: 'Storage Add-ons', basic: true, advanced: true, professional: true },
      { name: 'Help Centre', basic: true, advanced: true, professional: true },
      { name: 'Standard Support', basic: true, advanced: true, professional: true },
      { name: 'Priority Support', basic: false, advanced: true, professional: true },
      { name: 'Priority+ Support', basic: false, advanced: false, professional: true },
    ]
  }
];

// Feature Matrix Component
const FeatureMatrix = ({ tiers }) => {
  const [expanded, setExpanded] = useState(false);
  
  const renderCell = (value) => {
    if (value === true) {
      return <Check className="h-5 w-5 text-green-500 mx-auto" />;
    } else if (value === false) {
      return <X className="h-5 w-5 text-zinc-600 mx-auto" />;
    } else if (value === 'soon') {
      return <span className="text-xs text-amber-500 font-medium">Soon</span>;
    }
    return value;
  };

  return (
    <div className="mt-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-center gap-2 text-zinc-400 hover:text-white transition-colors py-3"
        data-testid="feature-comparison-toggle"
      >
        <span className="text-sm font-medium">Compare all features</span>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      
      {expanded && (
        <div className="mt-4 bg-card rounded-lg border border-zinc-800 overflow-hidden" data-testid="feature-matrix">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 bg-zinc-900/50">
                  <th className="text-left py-4 px-4 font-medium text-zinc-400 w-1/3">Feature</th>
                  <th className="text-center py-4 px-4 font-medium text-zinc-400">Basic</th>
                  <th className="text-center py-4 px-4 font-medium text-[#ffd108]">Advanced</th>
                  <th className="text-center py-4 px-4 font-medium text-zinc-400">Professional</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_COMPARISON.map((category, catIdx) => (
                  <>
                    <tr key={`cat-${catIdx}`} className="bg-zinc-800/50">
                      <td colSpan={4} className="py-2 px-4 font-semibold text-white text-xs uppercase tracking-wider">
                        {category.category}
                      </td>
                    </tr>
                    {category.features.map((feature, featIdx) => (
                      <tr 
                        key={`feat-${catIdx}-${featIdx}`} 
                        className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                      >
                        <td className="py-3 px-4 text-zinc-300">{feature.name}</td>
                        <td className="py-3 px-4 text-center">{renderCell(feature.basic)}</td>
                        <td className="py-3 px-4 text-center bg-[#ffd108]/5">{renderCell(feature.advanced)}</td>
                        <td className="py-3 px-4 text-center">{renderCell(feature.professional)}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const Login = () => {
  const { login, loginV2, selectTenant, pendingTenantSelection, tenants: authTenants } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  const [loading, setLoading] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: Select Tier, 2: Company Details
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [currency, setCurrency] = useState('AUD');
  const [trialPeriodDays, setTrialPeriodDays] = useState(0);
  const [pricingAvailable, setPricingAvailable] = useState(true); // New: track if pricing is configured
  
  // V2 Auth state - Default to email login when enabled
  const [useEmailLogin, setUseEmailLogin] = useState(false); // Default to Client ID login
  const [emailLoginEnabled, setEmailLoginEnabled] = useState(false);
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  const [v2Tenants, setV2Tenants] = useState([]);

  // Login form (legacy)
  const [loginForm, setLoginForm] = useState({
    clientId: '',
    username: '',
    password: ''
  });
  
  // V2 Login form (email-based)
  const [emailLoginForm, setEmailLoginForm] = useState({
    email: '',
    password: ''
  });

  // Setup form (for creating new tenant)
  const [selectedTier, setSelectedTier] = useState(null);
  const [setupForm, setSetupForm] = useState({
    clientId: '',
    companyName: '',
    adminUsername: '',
    adminPassword: '',
    adminFullName: '',
    adminEmail: ''
  });
  
  // Quote request form
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    companyName: '',
    contactName: '',
    position: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    estimatedUsers: '',
    estimatedOperators: '',
    estimatedEquipment: '',
    requirements: ''
  });

  // Get states for selected country
  const selectedCountryData = COUNTRIES.find(c => c.code === quoteForm.country);
  const availableStates = selectedCountryData?.states || [];

  // Handle country change - update phone code and reset state
  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    setQuoteForm(prev => ({
      ...prev,
      country: countryCode,
      state: '', // Reset state when country changes
      phone: country?.phoneCode ? `${country.phoneCode} ` : prev.phone
    }));
  };

  // Force dark mode on login/signup pages
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    // Restore previous theme when leaving the page
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Check if email login (v2 auth) is enabled
  useEffect(() => {
    const checkEmailLoginEnabled = async () => {
      try {
        const response = await axios.get(`${API_URL}/super-admin/auth-migration/feature-flags`);
        setEmailLoginEnabled(response.data.new_auth_enabled || false);
      } catch (error) {
        // If endpoint fails (not authenticated), check public status
        try {
          const publicResponse = await axios.get(`${API_URL}/auth/feature-flags`);
          setEmailLoginEnabled(publicResponse.data.new_auth_enabled || false);
        } catch {
          // Feature flags not available, keep email login disabled
          setEmailLoginEnabled(false);
        }
      }
    };
    checkEmailLoginEnabled();
  }, []);
  
  // Handle pending tenant selection from context
  useEffect(() => {
    if (pendingTenantSelection && authTenants?.length > 0) {
      // Filter to only show tenant user accounts (admin/dispatcher), not operator accounts
      const tenantUserAccounts = authTenants.filter(t => !t.is_operator);
      if (tenantUserAccounts.length > 1) {
        setV2Tenants(tenantUserAccounts);
        setShowTenantSelector(true);
      }
    }
  }, [pendingTenantSelection, authTenants]);

  // Fetch tier pricing when entering setup mode
  useEffect(() => {
    if (setupMode && tiers.length === 0) {
      fetchTierPricing();
    }
  }, [setupMode]);

  const fetchTierPricing = async () => {
    setTiersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/billing/tiers`);
      const tierData = response.data.tiers;
      const fetchedCurrency = response.data.currency || 'AUD';
      setCurrency(fetchedCurrency);
      setTrialPeriodDays(response.data.trial_period_days || 0);
      
      // Check if pricing is available (Stripe products configured)
      const isPricingAvailable = response.data.pricing_available !== false;
      setPricingAvailable(isPricingAvailable);
      
      if (!isPricingAvailable) {
        console.warn('Stripe pricing not configured. Missing tiers:', response.data.missing_tiers);
      }
      
      // Build tiers with dynamic pricing
      const dynamicTiers = DEFAULT_TIERS.map(defaultTier => {
        const stripeTier = tierData[defaultTier.id];
        if (stripeTier) {
          const extraUserPrice = stripeTier.extra_user_price || defaultTier.extraUserPrice;
          const extraOperatorPrice = stripeTier.extra_operator_price || defaultTier.extraOperatorPrice;
          
          return {
            ...defaultTier,
            price: stripeTier.base_price || defaultTier.price,
            extraUserPrice,
            extraOperatorPrice,
            features: [
              ...defaultTier.baseFeatures,
              `Extra User: $${extraUserPrice}/month`,
              `Extra Operator: $${extraOperatorPrice}/month`
            ]
          };
        }
        return {
          ...defaultTier,
          features: [
            ...defaultTier.baseFeatures,
            `Extra User: $${defaultTier.extraUserPrice}/month`,
            `Extra Operator: $${defaultTier.extraOperatorPrice}/month`
          ]
        };
      });
      
      setTiers(dynamicTiers);
    } catch (error) {
      console.error('Failed to fetch tier pricing:', error);
      setPricingAvailable(false); // Mark pricing as unavailable on error
      // Fall back to default tiers (but signup will be disabled)
      setTiers(DEFAULT_TIERS.map(t => ({
        ...t,
        features: [
          ...t.baseFeatures,
          `Extra User: $${t.extraUserPrice}/month`,
          `Extra Operator: $${t.extraOperatorPrice}/month`
        ]
      })));
    } finally {
      setTiersLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.clientId.toUpperCase(), loginForm.username, loginForm.password);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  // V2 Email Login Handler
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginV2(emailLoginForm.email, emailLoginForm.password);
      
      // Filter to only show tenant user accounts (admin/dispatcher), not operator accounts
      // Operator accounts should use the mobile/operator login portal instead
      const tenantUserAccounts = result.tenants?.filter(t => !t.is_operator) || [];
      
      if (tenantUserAccounts.length > 1) {
        // User has multiple tenant user accounts - show selector
        setV2Tenants(tenantUserAccounts);
        setShowTenantSelector(true);
        toast.info('Please select your organization');
      } else if (tenantUserAccounts.length === 1) {
        // Only one tenant user account - auto-select it and navigate
        const singleTenant = tenantUserAccounts[0];
        try {
          await selectTenant(singleTenant.client_id);
          toast.success(`Welcome back!`);
          window.location.href = '/dashboard';
        } catch (selectError) {
          console.error('Auto-select tenant failed:', selectError);
          toast.error('Failed to select organization. Please try again.');
        }
      } else if (tenantUserAccounts.length === 0 && result.tenants?.length > 0) {
        // User only has operator accounts, no tenant user accounts
        toast.error('No tenant accounts found. Please use the Operator Login for mobile access.');
      } else {
        // Single tenant was auto-selected by backend, navigate to dashboard
        toast.success('Welcome back!');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Login failed';
      if (errorDetail.includes('not yet enabled')) {
        toast.error('Email login is not yet available. Please use Client ID login.');
        setUseEmailLogin(false);
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Tenant Selection Handler
  const handleSelectUserTenant = async (tenant) => {
    setLoading(true);
    try {
      await selectTenant(tenant.client_id);
      setShowTenantSelector(false);
      toast.success(`Signed in to ${tenant.company_name}`);
      // Force a full page navigation to dashboard after tenant selection
      // This ensures all components use the new token
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select organization');
      setLoading(false);
    }
  };

  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
    setSetupStep(2);
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    if (setupForm.clientId.length !== 6) {
      toast.error('Client ID must be exactly 6 characters');
      return;
    }
    if (setupForm.adminPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      // Create Stripe checkout session for the selected tier
      const response = await axios.post(`${API_URL}/billing/signup-checkout`, {
        tier: selectedTier.id,
        client_id: setupForm.clientId.toUpperCase(),
        company_name: setupForm.companyName,
        admin_username: setupForm.adminUsername,
        admin_password: setupForm.adminPassword,
        admin_full_name: setupForm.adminFullName,
        admin_email: setupForm.adminEmail
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  const resetSetup = () => {
    setSetupMode(false);
    setSetupStep(1);
    setSelectedTier(null);
    setSetupForm({
      clientId: '',
      companyName: '',
      adminUsername: '',
      adminPassword: '',
      adminFullName: '',
      adminEmail: ''
    });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteSubmitting(true);
    
    try {
      const countryName = COUNTRIES.find(c => c.code === quoteForm.country)?.name || quoteForm.country;
      
      await axios.post(`${API_URL}/quotes/request`, {
        company_name: quoteForm.companyName,
        contact_name: quoteForm.contactName,
        position: quoteForm.position,
        email: quoteForm.email,
        phone: quoteForm.phone,
        country: countryName,
        state: quoteForm.state,
        estimated_users: parseInt(quoteForm.estimatedUsers) || 0,
        estimated_operators: parseInt(quoteForm.estimatedOperators) || 0,
        estimated_equipment: parseInt(quoteForm.estimatedEquipment) || 0,
        requirements: quoteForm.requirements
      });
      
      toast.success("Quote request submitted!", {
        description: "We'll be in touch within 1-2 business days."
      });
      
      setQuoteModalOpen(false);
      setQuoteForm({
        companyName: '',
        contactName: '',
        position: '',
        email: '',
        phone: '',
        country: '',
        state: '',
        estimatedUsers: '',
        estimatedOperators: '',
        estimatedEquipment: '',
        requirements: ''
      });
    } catch (error) {
      toast.error("Failed to submit quote request", {
        description: error.response?.data?.detail || "Please try again later."
      });
    } finally {
      setQuoteSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Login Card */}
      {!setupMode && !showTenantSelector && (
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">EQUIPR CORE</CardTitle>
            <CardDescription>Run your business from the CORE</CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Login Mode Toggle - Only show tabs if email login is enabled */}
            {emailLoginEnabled && (
              <div className="flex mb-4 p-1 bg-zinc-800 rounded-lg">
                <button
                  type="button"
                  onClick={() => setUseEmailLogin(true)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    useEmailLogin 
                      ? 'bg-[#ffd108] text-black' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="login-mode-email"
                >
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setUseEmailLogin(false)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    !useEmailLogin 
                      ? 'bg-[#ffd108] text-black' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="login-mode-legacy"
                >
                  Client ID
                </button>
              </div>
            )}
            
            {/* Legacy Login Form (Client ID + Username) - Show when email login disabled OR when Client ID tab selected */}
            {(!emailLoginEnabled || !useEmailLogin) && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Client ID</Label>
                  <Input
                    data-testid="client-id"
                    value={loginForm.clientId}
                    onChange={(e) => setLoginForm({ ...loginForm, clientId: e.target.value.toUpperCase() })}
                    placeholder="ABC001"
                    maxLength={6}
                    className="font-mono uppercase"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-overline">Username</Label>
                  <Input
                    data-testid="username"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    placeholder="Your username"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-overline">Password</Label>
                  <Input
                    data-testid="password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="********"
                    required
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-muted-foreground hover:text-[#ffd108] transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-volt" 
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>
            )}
            
            {/* V2 Email Login Form - Only show when email login is enabled AND email tab selected */}
            {emailLoginEnabled && useEmailLogin && (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Email</Label>
                  <Input
                    data-testid="email-login-email"
                    type="email"
                    value={emailLoginForm.email}
                    onChange={(e) => setEmailLoginForm({ ...emailLoginForm, email: e.target.value })}
                    placeholder="you@company.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-overline">Password</Label>
                  <Input
                    data-testid="email-login-password"
                    type="password"
                    value={emailLoginForm.password}
                    onChange={(e) => setEmailLoginForm({ ...emailLoginForm, password: e.target.value })}
                    placeholder="********"
                    required
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-muted-foreground hover:text-[#ffd108] transition-colors"
                      data-testid="email-forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-volt" 
                  disabled={loading}
                  data-testid="email-login-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Sign In with Email
                    </>
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t text-center space-y-3">
              <Link to="/free-trial" className="block">
                <Button
                  className="w-full btn-volt"
                  data-testid="start-free-trial-link"
                >
                  <Gift className="h-4 w-4 mr-2" />
                  Start 14-Day Free Trial
                </Button>
              </Link>
              
              <button
                onClick={() => setSetupMode(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="setup-company-link"
              >
                <Building2 className="h-4 w-4 inline mr-1" />
                Existing customer? Set up your organization
              </button>
              
              <div className="pt-2">
                <Link to="/mobile/login" className="block">
                  <Button
                    variant="outline"
                    className="w-full"
                    data-testid="operator-portal-link"
                  >
                    <HardHat className="h-4 w-4 mr-2" />
                    Operator Login
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tenant Selector - Shown when user has multiple tenants */}
      {showTenantSelector && (
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="tenant-selector-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">Select Organization</CardTitle>
            <CardDescription>You have access to multiple organizations</CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3">
              {v2Tenants.map((tenant) => (
                <button
                  key={tenant.client_id}
                  onClick={() => handleSelectUserTenant(tenant)}
                  disabled={loading}
                  className="w-full p-4 rounded-lg border border-zinc-700 hover:border-[#ffd108] hover:bg-zinc-800/50 transition-all text-left group"
                  data-testid={`tenant-option-${tenant.client_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-zinc-400 group-hover:text-[#ffd108]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white">{tenant.company_name}</div>
                      <div className="text-xs text-zinc-400">
                        {tenant.client_id} • {tenant.role}
                        {tenant.is_operator && ' • Operator'}
                      </div>
                    </div>
                    {loading && (
                      <Loader2 className="h-4 w-4 animate-spin text-[#ffd108]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t text-center">
              <button
                onClick={() => {
                  setShowTenantSelector(false);
                  setUseEmailLogin(true);
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="back-to-login"
              >
                <ArrowLeft className="h-4 w-4 inline mr-1" />
                Sign in with different account
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Mode - Tier Selection */}
      {setupMode && setupStep === 1 && (
        <div className="w-full max-w-4xl relative z-10">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3134b3b3-be40-4138-91b9-92f518baf2f3/artifacts/jfduu0wk_EQUIPR%20Icon300X300px.png" 
                  alt="EQUIPR" 
                  className="w-full h-full object-cover" 
                />
              </div>
            </div>
            <h1 className="text-3xl font-heading font-bold text-white mb-2">Choose Your Plan</h1>
            <p className="text-zinc-400">Select the plan that best fits your business needs</p>
            {trialPeriodDays > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full">
                <Gift className="h-5 w-5" />
                <span className="font-medium">{trialPeriodDays}-day free trial included!</span>
              </div>
            )}
          </div>

          {/* Warning when pricing is not configured */}
          {!pricingAvailable && !tiersLoading && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
              <p className="text-amber-400 font-medium">Pricing is currently unavailable</p>
              <p className="text-amber-400/70 text-sm mt-1">Please contact support or try again later.</p>
            </div>
          )}

          {tiersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
            </div>
          ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier) => {
              const isProfessional = tier.id === 'professional';
              const isClickable = pricingAvailable && !isProfessional;
              
              return (
              <Card 
                key={tier.id}
                className={`relative border-2 transition-all ${
                  isClickable
                    ? 'cursor-pointer hover:border-[#ffd108]' 
                    : isProfessional 
                      ? 'cursor-default'
                      : 'cursor-not-allowed opacity-60'
                } ${tier.popular ? 'border-[#ffd108]' : 'border-zinc-800'}`}
                onClick={() => isClickable && handleSelectTier(tier)}
              >
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ffd108] text-black">
                    Most Popular
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-heading text-xl">{tier.name}</CardTitle>
                  <div className="mt-2">
                    {isProfessional ? (
                      <>
                        <span className="text-2xl font-bold text-[#ffd108]">Custom Pricing</span>
                        <p className="text-sm text-muted-foreground mt-1">Tailored to your needs</p>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">${tier.price}</span>
                        <span className="text-muted-foreground">/month {currency !== 'AUD' && `(${currency})`}</span>
                      </>
                    )}
                  </div>
                  <CardDescription className="mt-2">{tier.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {isProfessional ? (
                    <Button 
                      className="w-full mt-6"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuoteModalOpen(true);
                      }}
                    >
                      Request a Quote
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full mt-6 ${tier.popular ? 'btn-volt' : ''}`}
                      variant={tier.popular ? 'default' : 'outline'}
                      disabled={!pricingAvailable}
                    >
                      {pricingAvailable ? `Select ${tier.name}` : 'Unavailable'}
                    </Button>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
          )}

          {/* Feature Comparison Matrix */}
          {!tiersLoading && (
            <FeatureMatrix tiers={tiers} />
          )}

          <div className="mt-6 text-center">
            <button
              onClick={resetSetup}
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4 inline mr-1" />
              Back to login
            </button>
          </div>
        </div>
      )}

      {/* Setup Mode - Company Details */}
      {setupMode && setupStep === 2 && selectedTier && (
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#ffd108] rounded-sm">
                <Truck className="h-8 w-8 text-black" />
              </div>
            </div>
            <CardTitle className="font-heading text-xl">Set Up Your Company</CardTitle>
            <CardDescription>
              <Badge className="mt-2" variant="secondary">
                {selectedTier.name} Plan - ${selectedTier.price}/month
              </Badge>
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSetupSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Client ID (6 characters)</Label>
                <Input
                  value={setupForm.clientId}
                  onChange={(e) => setSetupForm({ ...setupForm, clientId: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) })}
                  placeholder="ABC001"
                  maxLength={6}
                  className="font-mono uppercase"
                  required
                />
                <p className="text-xs text-muted-foreground">This will be your unique company identifier</p>
              </div>

              <div className="space-y-2">
                <Label className="label-overline">Company Name</Label>
                <Input
                  value={setupForm.companyName}
                  onChange={(e) => setSetupForm({ ...setupForm, companyName: e.target.value })}
                  placeholder="Your Company Name"
                  required
                />
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-3">Admin Account Details</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Username</Label>
                  <Input
                    value={setupForm.adminUsername}
                    onChange={(e) => setSetupForm({ ...setupForm, adminUsername: e.target.value })}
                    placeholder="admin"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Password</Label>
                  <Input
                    type="password"
                    value={setupForm.adminPassword}
                    onChange={(e) => setSetupForm({ ...setupForm, adminPassword: e.target.value })}
                    placeholder="Min 8 chars"
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-overline">Full Name</Label>
                <Input
                  value={setupForm.adminFullName}
                  onChange={(e) => setSetupForm({ ...setupForm, adminFullName: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  type="email"
                  value={setupForm.adminEmail}
                  onChange={(e) => setSetupForm({ ...setupForm, adminEmail: e.target.value })}
                  placeholder="admin@company.com"
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full btn-volt" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Continue to Payment</>
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setSetupStep(1)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Change Plan
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Quote Request Modal */}
      <Dialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Request a Quote</DialogTitle>
            <DialogDescription>
              Tell us about your business and we'll create a custom Professional plan quote for you.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleQuoteSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote-company">Company Name *</Label>
                <Input
                  id="quote-company"
                  value={quoteForm.companyName}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Your company name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-contact">Contact Name *</Label>
                <Input
                  id="quote-contact"
                  value={quoteForm.contactName}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, contactName: e.target.value }))}
                  placeholder="Your name"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote-position">Position / Role</Label>
                <Input
                  id="quote-position"
                  value={quoteForm.position}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, position: e.target.value }))}
                  placeholder="e.g., Operations Manager"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-email">Email *</Label>
                <Input
                  id="quote-email"
                  type="email"
                  value={quoteForm.email}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote-country">Country *</Label>
                <Select value={quoteForm.country} onValueChange={handleCountryChange} required>
                  <SelectTrigger id="quote-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-state">State / Province {availableStates.length > 0 ? '*' : ''}</Label>
                <Select 
                  value={quoteForm.state} 
                  onValueChange={(value) => setQuoteForm(prev => ({ ...prev, state: value }))}
                  disabled={availableStates.length === 0}
                >
                  <SelectTrigger id="quote-state">
                    <SelectValue placeholder={availableStates.length === 0 ? "Select country first" : "Select state/province"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStates.map(state => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quote-phone">Phone</Label>
              <Input
                id="quote-phone"
                type="tel"
                value={quoteForm.phone}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder={selectedCountryData?.phoneCode ? `${selectedCountryData.phoneCode} XXX XXX XXX` : "Select country first"}
              />
            </div>
            
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-muted-foreground mb-3">Estimated business size</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quote-users">Users *</Label>
                  <Input
                    id="quote-users"
                    type="number"
                    min="1"
                    value={quoteForm.estimatedUsers}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, estimatedUsers: e.target.value }))}
                    placeholder="5"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Admin/Dispatchers</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quote-operators">Operators *</Label>
                  <Input
                    id="quote-operators"
                    type="number"
                    min="0"
                    value={quoteForm.estimatedOperators}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, estimatedOperators: e.target.value }))}
                    placeholder="15"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Field workers</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quote-equipment">Assets *</Label>
                  <Input
                    id="quote-equipment"
                    type="number"
                    min="0"
                    value={quoteForm.estimatedEquipment}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, estimatedEquipment: e.target.value }))}
                    placeholder="25"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Fleet size</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quote-requirements">Specific Requirements</Label>
              <Textarea
                id="quote-requirements"
                value={quoteForm.requirements}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, requirements: e.target.value }))}
                placeholder="Tell us about any specific features or integrations you need..."
                rows={3}
              />
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setQuoteModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" disabled={quoteSubmitting}>
                {quoteSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
